/* ---------------------------------------------------------------------------
   Cross out variant combinations that do not exist.

   Warehouse draws every option value against every other one, whether or not a
   variant exists for the pair. On a product with a partial matrix that lets a
   customer build a combination that was never made: the wood pulp banneton has
   four shapes and two sizes but only six variants, so picking Round smooth with
   500g still selected lands on a disabled button reading Unavailable. That is a
   dead end on a page that was converting a second earlier.

   The rule is progressive, which is what Shopify's own pickers do. The first
   option is never restricted. The second is restricted by the first, the third
   by the first two, and so on. That guarantees the customer can never walk into
   a combination that does not exist, because each step only offers what the
   previous steps still allow.

   Out of stock is deliberately NOT the same thing. A variant that exists but has
   sold out stays selectable, because Sold out is information the customer wants.
   Only combinations that were never made get crossed out.

   Two things the first version got wrong, recorded so they are not repeated:

   1. The theme replaces the whole picker on every change (product-rerender), so
      cached node references go stale and the marks vanish the moment they matter
      most. Nothing is cached: the DOM is re-read on every pass, and a
      MutationObserver on #main re-runs after the theme has finished redrawing.
      The observer watches childList only, so setting a class or the disabled
      property cannot feed back into it.

   2. A backslash-d written inside a JavaScript string is not an escape, it
      collapses to a plain d, so a RegExp built from a string silently matches
      nothing. No RegExp is built from a string here.

   The variant matrix comes from /products/<handle>.js rather than being printed
   into the page, so this works on every product without touching a template.
--------------------------------------------------------------------------- */
(function () {
  'use strict';

  if (!document.querySelector('variant-picker')) return;

  var parts = window.location.pathname.split('/products/');
  if (parts.length < 2) return;
  var handle = parts[1].split('/')[0].split('?')[0].split('#')[0];
  if (!handle) return;

  var base = (window.routes && window.routes.rootUrlWithoutSlash) || '';
  var product = null;
  var timer = null;
  var repairs = 0;

  fetch(base + '/products/' + handle + '.js', { credentials: 'same-origin' })
    .then(function (r) { return r.ok ? r.json() : null; })
    .then(function (p) {
      if (!p || !p.variants || !p.options || p.options.length < 2) return;
      product = p;
      injectStyle();
      document.addEventListener('change', onChange, true);
      var main = document.getElementById('main');
      if (main && window.MutationObserver) {
        new MutationObserver(schedule).observe(main, { childList: true, subtree: true });
      }
      schedule();
    })
    .catch(function () { /* leave the picker exactly as the theme drew it */ });

  function onChange(e) {
    var n = e.target && e.target.name;
    if (n && n.indexOf('option') === 0) { repairs = 0; schedule(); }
  }

  function schedule() {
    clearTimeout(timer);
    timer = setTimeout(apply, 60);
  }

  /* Read the picker fresh every time. groups[i] is the list of choices for
     option i+1, each carrying its input, its label and the option value text. */
  function readGroups() {
    var picker = document.querySelector('variant-picker');
    if (!picker) return null;
    var groups = [];

    for (var i = 0; i < product.options.length; i++) {
      var name = 'option' + (i + 1);
      var values = product.options[i].values || [];
      var radios = picker.querySelectorAll('input[type="radio"][name="' + name + '"]');
      var select = picker.querySelector('select[name="' + name + '"]');
      var group = [];

      if (radios.length) {
        for (var r = 0; r < radios.length; r++) {
          group.push({
            kind: 'radio',
            el: radios[r],
            label: picker.querySelector('label[for="' + radios[r].id + '"]'),
            value: valueFor(values, radios[r].id, name, r)
          });
        }
      } else if (select) {
        for (var s = 0; s < select.options.length; s++) {
          group.push({ kind: 'select', el: select.options[s], owner: select, label: null, value: values[s] });
        }
      }

      if (!group.length) return null;   // markup we do not recognise: do nothing
      groups.push(group);
    }
    return groups;
  }

  /* The radio value attribute is an option-value id, not the text, so the value
     is recovered from the input's id, which Warehouse suffixes -option1-1,
     -option1-2 and so on: one-indexed, in the same order as the values array.
     Falls back to DOM order if the id is ever shaped differently. */
  function valueFor(values, id, name, idx) {
    var n = idx;
    var marker = '-' + name + '-';
    var at = id ? id.lastIndexOf(marker) : -1;
    if (at > -1) {
      var parsed = parseInt(id.slice(at + marker.length), 10);
      if (parsed > 0) n = parsed - 1;
    }
    return values[n] != null ? values[n] : values[idx];
  }

  function isOn(o) { return o.kind === 'radio' ? o.el.checked : o.el.selected; }

  function chosenValue(group) {
    for (var i = 0; i < group.length; i++) if (isOn(group[i])) return group[i].value;
    return null;
  }

  function exists(probe, upTo) {
    return product.variants.some(function (v) {
      for (var k = 0; k <= upTo; k++) {
        if (probe[k] != null && v.options[k] !== probe[k]) return false;
      }
      return true;
    });
  }

  function apply() {
    var groups = readGroups();
    if (!groups) return;

    var chosen = [];
    for (var c = 0; c < groups.length; c++) chosen.push(chosenValue(groups[c]));

    for (var i = 0; i < groups.length; i++) {
      for (var j = 0; j < groups[i].length; j++) {
        var o = groups[i][j];
        var probe = chosen.slice(0, i);
        probe[i] = o.value;
        setState(o, exists(probe, i));
      }
    }

    /* If a later option was left pointing at something the new first choice does
       not allow, move it to the first thing that is allowed and let the theme
       re-render from its own change handler. Without this the customer still
       sees Unavailable for a moment after switching shape. The repair counter
       stops a malformed matrix turning this into a loop; it resets whenever the
       customer themselves changes something. */
    if (repairs > 4) return;

    for (var g = 1; g < groups.length; g++) {
      var current = null;
      for (var k = 0; k < groups[g].length; k++) {
        if (isOn(groups[g][k])) { current = groups[g][k]; break; }
      }
      if (!current || !current.el.disabled) continue;

      var first = null;
      for (var m = 0; m < groups[g].length; m++) {
        if (!groups[g][m].el.disabled) { first = groups[g][m]; break; }
      }
      if (!first) continue;

      repairs++;
      if (first.kind === 'radio') {
        first.el.checked = true;
        first.el.dispatchEvent(new Event('change', { bubbles: true }));
      } else {
        first.el.selected = true;
        first.owner.dispatchEvent(new Event('change', { bubbles: true }));
      }
      return;   // the observer will run this again once the theme has redrawn
    }
  }

  function setState(o, ok) {
    o.el.disabled = !ok;
    if (o.label) {
      if (ok) o.label.classList.remove('ssc-opt-gone');
      else o.label.classList.add('ssc-opt-gone');
      o.label.setAttribute('aria-disabled', ok ? 'false' : 'true');
    }
  }

  /* Styled here rather than in a stylesheet because the class only ever exists
     when this script has run. Muted with a colour and a rule through it, not with
     opacity: the whole accessibility pass was undoing opacity muting, and a
     crossed-out option still has to be readable enough to tell you what you are
     not getting. */
  function injectStyle() {
    if (document.getElementById('ssc-opt-gone-style')) return;
    var s = document.createElement('style');
    s.id = 'ssc-opt-gone-style';
    s.textContent =
      '.ssc-opt-gone{position:relative;color:#8A9280 !important;' +
      'border-color:#DDE0D6 !important;cursor:not-allowed}' +
      '.ssc-opt-gone::after{content:"";position:absolute;left:6%;right:6%;top:50%;' +
      'height:1px;background:#B6BCAC;transform:rotate(-14deg);pointer-events:none;z-index:2}';
    document.head.appendChild(s);
  }
})();